import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, UserPlus, MessageSquare, FileText, Users, Repeat, PoundSterling, Percent, CheckSquare, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Section, cardCls, EmptyState } from "@/components/admin/ui";

const PIPELINE = ["New", "Discovery", "Proposal", "Follow-Up", "Negotiation", "Won", "Lost"];

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className={`${cardCls} p-5`}>
      <div className="flex items-center gap-2 text-bd-ivory/40 mb-2"><Icon size={15} className="text-bd-gold" /><span className="text-xs uppercase tracking-wider">{label}</span></div>
      <p className="font-serif text-3xl text-bd-ivory">{value}</p>
    </div>
  );
}

export default function BusinessServices() {
  const [m, setM] = useState(null);

  useEffect(() => { api.get("/admin/business-services/metrics").then(({ data }) => setM(data)).catch(() => setM(false)); }, []);

  const gbp = (n) => "£" + Number(n || 0).toLocaleString("en-GB");
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");

  return (
    <div data-testid="admin-business-services">
      <Section title="BDVG Business Services" subtitle="Client services pipeline, consultations and engagements — separate from individual Black Diamond ventures.">
        {m === false ? <EmptyState>Could not load metrics.</EmptyState> : !m ? <EmptyState>Loading…</EmptyState> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat testid="bs-stat-new" icon={UserPlus} label="New leads" value={m.new_leads} />
              <Stat testid="bs-stat-consultations" icon={MessageSquare} label="Consultations" value={m.consultations} />
              <Stat testid="bs-stat-proposals" icon={FileText} label="Proposals" value={m.proposals} />
              <Stat testid="bs-stat-clients" icon={Users} label="Active clients" value={m.active_clients} />
              <Stat testid="bs-stat-monthly" icon={Repeat} label="Monthly clients" value={m.monthly_clients} />
              <Stat testid="bs-stat-revenue" icon={PoundSterling} label="Won revenue" value={gbp(m.revenue)} />
              <Stat testid="bs-stat-conversion" icon={Percent} label="Conversion rate" value={`${m.conversion_rate}%`} />
              <Stat testid="bs-stat-tasks" icon={CheckSquare} label="Open tasks" value={m.open_tasks} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div className={`${cardCls} p-6`}>
                <h2 className="font-serif text-xl text-bd-ivory mb-1">Pipeline</h2>
                <p className="text-xs text-bd-ivory/40 mb-5">Pipeline value (open): <span className="text-bd-gold">{gbp(m.pipeline_value)}</span> · Monthly recurring: <span className="text-bd-gold">{gbp(m.monthly_recurring)}</span></p>
                <div className="space-y-2.5">
                  {PIPELINE.map((s) => {
                    const count = m.by_stage?.[s] || 0;
                    const max = Math.max(1, ...Object.values(m.by_stage || {}));
                    return (
                      <div key={s} data-testid={`bs-pipeline-${s.toLowerCase()}`} className="flex items-center gap-3">
                        <span className="text-xs text-bd-ivory/50 w-24 flex-shrink-0">{s}</span>
                        <div className="flex-1 h-2 bg-white/5"><div className="h-full bg-bd-gold/70" style={{ width: `${(count / max) * 100}%` }} /></div>
                        <span className="text-sm text-bd-ivory/80 w-6 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={`${cardCls} p-6`}>
                <h2 className="font-serif text-xl text-bd-ivory mb-5">Lead sources</h2>
                {Object.keys(m.by_source || {}).length === 0 ? <p className="text-sm text-bd-ivory/40">No leads yet.</p> : (
                  <div className="space-y-3">
                    {Object.entries(m.by_source).sort((a, b) => b[1] - a[1]).map(([src, c]) => (
                      <div key={src} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                        <span className="text-bd-ivory/70">{src}</span><span className="text-bd-gold">{c}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={`${cardCls} mt-6`}>
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3"><Briefcase size={18} className="text-bd-gold" /><h2 className="font-serif text-xl text-bd-ivory">Recent leads</h2></div>
                <Link to="/admin/crm" data-testid="bs-view-crm" className="text-xs text-bd-gold hover:text-bd-gold2 flex items-center gap-1">Open in CRM <ArrowRight size={13} /></Link>
              </div>
              {(!m.recent || m.recent.length === 0) ? <EmptyState>No business services leads yet. They arrive automatically from the website enquiry form.</EmptyState> : (
                <div className="divide-y divide-white/5">
                  {m.recent.map((c) => (
                    <div key={c.id} data-testid={`bs-lead-${c.id}`} className="flex items-center gap-4 p-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-bd-ivory truncate">{c.name}{c.company ? ` · ${c.company}` : ""}</p>
                        <p className="text-xs text-bd-ivory/40 truncate">{(c.services_required || []).join(", ") || c.industry || "—"}</p>
                      </div>
                      <span className="text-[0.6rem] uppercase tracking-wider border border-bd-gold/40 text-bd-gold px-2 py-0.5">{c.stage}</span>
                      <span className="text-xs text-bd-ivory/30 whitespace-nowrap">{fmtDate(c.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
